import React, { useState, useEffect } from 'react';
import { createModel } from 'hox';
import useGlobalModel from '@/model/globalModel';
import { assetsMoveService } from '../../assetsMoveService';
import { convertRes2Blob } from '@/utils/exportFile';
import { message } from 'antd';
import { useLockFn } from 'ahooks';
import {
	ISCMAssetsMoveFormFields,
	ISCMAssetsMoveInsert,
	ISCMAssetsMoveUpdate,
	ISCMSDcGoodsDetail,
	ISCMSDcGoodsOne,
} from '../../assetsMoveTypes';
import { globalPrompt } from '@/components/message';
import { ISCMSDcSelectGoods } from './AddGoodsModal';
import { CLIENT_APP_CODE } from '@/utils/constants';
import { formatNum, momentFormat } from '@/utils/utils';
import useNavModel from '@/model/navModel';
import { useDictModel } from '@/views/base/dict/dictModel';

export type ISCMSDcDescType = {
	numAll: number;
	amountAll: number;
	actualAmount: number;
	[key: string]: any;
};
const initDesc = {
	actualAmount: 0, //实际总价合计 （数量*单价）
	//对应展示字段
	numAll: 0, //调拨数量
	amountAll: 0, //调拨总计   （去除下浮）
};

export const useAssetsMoveDetailsModel = createModel(function () {
	const {
		user: { userName },
	} = useGlobalModel();
    const { getSelectedDictValueOne,getSelectedDictOne, getTreeNodeName } = useDictModel(() => []);

	const initFormVal = {
		// createUser: userName,
		applier: userName,
		branchOut: CLIENT_APP_CODE,
		discountTotalAmount: '0.00', //优惠总额
		favorableAmount: '0', //整单优惠
		aggregateAmount: '0.00', //应付金额
		discountRate: '0', //整单下浮
	} as ISCMAssetsMoveFormFields;
    const { selectedNavId, replaceNav, findNav } = useNavModel()
	//编辑缓存
	const [editFormVal, setEditFormVal] = useState<ISCMAssetsMoveFormFields>(initFormVal);
	const [editGoodsList, setEditGoodsList] = useState<ISCMSDcGoodsDetail[]>([]);
	const [editDesc, setEditDesc] = useState<ISCMSDcDescType>(initDesc);
    const [departmentOutValue, setDepartmentOutValue] = useState<string>('');   //用于搜索展示的数据
	//新增缓存
	const [addFormVal, setAddFormVal] = useState<ISCMAssetsMoveFormFields>(initFormVal);
	const [addGoodsList, setAddGoodsList] = useState<ISCMSDcGoodsDetail[]>([]);
	const [addDesc, setAddDesc] = useState<ISCMSDcDescType>(initDesc);

	// 控制先保存
	const [saveBtn, setSaveBtn] = useState<boolean>(false);
	// 保存按钮的loading效果
	const [saveBtnLoading, setSaveBtnLoading] = useState<boolean>(false);

	useEffect(() => {
		let newDesc: ISCMSDcDescType = { ...initDesc };
		editGoodsList.forEach((item) => {
			newDesc.numAll += Number(item.num);
			newDesc.amountAll += Number(item.amount);
			newDesc.actualAmount += Number(item.num) * Number(item.price);
		});
		setEditDesc(newDesc);
	}, [editGoodsList]);

	useEffect(() => {
		let newDesc: ISCMSDcDescType = { ...initDesc };
		addGoodsList.forEach((item) => {
			newDesc.numAll += Number(item.num);
			newDesc.amountAll += Number(item.amount);
			newDesc.actualAmount += Number(item.num) * Number(item.price);
		});
		setAddDesc(newDesc);
	}, [addGoodsList]);

	useEffect(() => {
		resetAddCatch();
		// setAddFormVal({ ...initFormVal, createUser: userName });
		setAddFormVal({ ...initFormVal});
	}, []);

	//表单新增修改
	const updateAddFormVal = (val: object | null) => {
		val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal(initFormVal);
	};
	//表单编辑修改
	const updateEditFormVal = (val: object | null) => {
		val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal(initFormVal);
	};
    //申请单号列表
    const [applyList, setApplyList] = useState<any>([]);
	//重置缓存
	const resetEditCatch = () => {
		updateEditFormVal(null); //表单
		setEditGoodsList([]); //详情商品列表
		setEditSelectGoods([]); //弹框已选商品
	};
	const resetAddCatch = () => {
		updateAddFormVal(null);
		setAddGoodsList([]);
		setAddSelectGoods([]);
	};

	//获取一条数据
	const getSDcDetailOne = async (id: string) => {
		let { retData } = await assetsMoveService.one(id);
		let { details, ...formVal } = retData;
		if (retData) {
			setEditGoodsList(() => details.map((item) => ({ ...item,key: item.id, temporaryId: new Date().getTime() + Math.floor(Math.random() * 10000),})));
			let fileList: any = []
            const { md5, fileName } = formVal
            if (md5) {
                fileList = [
                    {
                        uid: md5,
                        status: 'done',
                        name: fileName,
                        response: {
                            retData: md5,
                        },
                    },
                ]
            }
            formVal.fileList = fileList
			setEditFormVal({ ...formVal,createTime:momentFormat(formVal.createTime)??'' });
		}
		return retData;
	};

	//表单物资校验 (开单数量不能为0)
	const verifyGoods = (mode: string): boolean => {
		let goods = mode === 'edit' ? editGoodsList : addGoodsList;
		if (!goods.length) {
			message.warning('请添加物品明细');
			return false;
		}
		let zeroNum = goods.filter((item) => !+item.num);
		let warehouseNum = goods.filter((item) => !item.warehouseCode||!item.shelfCode);
		if (zeroNum.length) {
			message.warning(
				`【${zeroNum.map((item) => item.materialName).join()}】物资的开单数量不能为0`
			);
			return false;
		}
		if (warehouseNum.length) {
			message.warning(
				`【${warehouseNum.map((item) => item.materialName).join()}】物资的仓库及货位不能为空`
			);
			return false;
		}
		return true;
	};

	//新增 Api
	const insertSDc = useLockFn(async (val: ISCMAssetsMoveFormFields) => {
		setSaveBtnLoading(true);
		try {
			let data: ISCMAssetsMoveInsert = {
				...val,
				details: addGoodsList,
				type:'assets.transfer'
			};
			let { retData } = await assetsMoveService.insert(data);
			globalPrompt("message", { text: "物资转移新增成功", type: "success" })
			setSaveBtnLoading(false);
			replaceNav(selectedNavId, '2-5')
			resetAddCatch();
			return retData;
		} catch (error) {
			setSaveBtnLoading(false);
		}
	});

	//编辑 Api
	const updateSDc = useLockFn(async (val: ISCMAssetsMoveFormFields & { id: string }) => {
		setSaveBtnLoading(true);
		try {
			let data: ISCMAssetsMoveUpdate = {
				...val,
				id: editFormVal.id,
				details: editGoodsList,
				type:'assets.transfer'
			};
			let { retData } = await assetsMoveService.update(data);
			globalPrompt("message", { text: "物资转移编辑成功", type: "success" })
			setSaveBtnLoading(false);
			replaceNav(selectedNavId, '2-5')
			resetEditCatch()
			return retData;
		} catch (error) {
			setSaveBtnLoading(false);
		}
	});

	/**
	 * 商品新增
	 */
	const [addGoodsModal, setAddGoodsModal] = useState<boolean>(false); //商品弹框
	const [editSelectGoods, setEditSelectGoods] = useState<ISCMSDcSelectGoods[]>([]); //已选的商品
	const [addSelectGoods, setAddSelectGoods] = useState<ISCMSDcSelectGoods[]>([]); //已选的商品
	// 操作数据先保存
	const [buttonControl, setButtonControl] = useState<boolean>(false);

	//添加商品  默认数量字段为1
	const add = async (record: ISCMSDcSelectGoods, mode: string) => {
		const isEdit: boolean = mode === 'edit';
		isEdit
			? setEditSelectGoods((origin) => [...origin, { ...record, sDcNum: 1 }])
			: setAddSelectGoods((origin) => [...origin, { ...record, sDcNum: 1 }]);
	};

	//删除商品 选中的
	const remove = (record: ISCMSDcSelectGoods, mode: string) => {
		mode === 'edit'
			? setEditSelectGoods((origin) => origin.filter((item) => item.id !== record.id))
			: setAddSelectGoods((origin) => origin.filter((item) => item.id !== record.id));
	};

	//选中的物资转商品
	const transformsToSDcGoods = useLockFn(async (selectList: ISCMSDcSelectGoods[], mode: string) => {
		// debugger;
		if (mode === 'edit') {
			setButtonControl(true);
			setSaveBtn(true);
		}
		const isEdit: boolean = mode === 'edit';
		let codeList: string[] = [];
		let goodsList = mode === 'add' ? addSelectGoods : editSelectGoods;
		goodsList.forEach((item) => {
			codeList.push(item.materialCode);
		});
		if(codeList.length === 0){
            setAddGoodsModal(false)
            return
        }
		const newDetailData:any = [];
		selectList.forEach((item) => {
			newDetailData.push({
				materialCode:item.materialCode,
				barCode:item.barCode,
				materialName:item.materialName,
				model:item.model,
				typeCode:item.typeCode,
				code:item.code,
				price:item.price,
				unitCode:item.unitCode,
				detailsId:item.id,
				num:item.num,
				// warehouseName:item.warehouseName,
				amount:item.amount??item.price,
				// warehouseCode:item.warehouseCode,
				// shelfCode:item.shelfCode,
				note:"",
				temporaryId: new Date().getTime() + Math.floor(Math.random() * 10000),
				beforeDeposit:item.deposit,
				beforeUser:item.user,
				beforeUserCode:item.userCode,
				typeName:item.typeName,
				unitName:getTreeNodeName(item.unitCode) ?? '',
				brandName:item.brandName,
				md5:item.md5,
				fileName:item.fileName,
				warehouseCode:null,
				warehouseName:null,
				shelfCode:null,
			});
		});

		isEdit
			? setEditGoodsList((origin) => [...origin, ...newDetailData])
			: setAddGoodsList((origin) => [...origin, ...newDetailData]);
		isEdit ? setEditSelectGoods([]) : setAddSelectGoods([]);
		setAddGoodsModal(false);
	});

	/**
	 * 打印
	 */
	const [print, setPrint] = useState<boolean>(false);
	const [historyVisible, setHistoryVisible] = useState<boolean>(false);
	const [historyRow, setHistoryRow] = useState<any>({});

	return {
		editFormVal,
		addFormVal,
		addGoodsModal,
		setAddGoodsModal,
		updateAddFormVal,
		updateEditFormVal,
		resetEditCatch,
		resetAddCatch,
		/**api */
		insertSDc,
		updateSDc,
		getSDcDetailOne,
		/**商品详情 */
		editSelectGoods,
		setEditSelectGoods,
		addSelectGoods,
		setAddSelectGoods,
		addGoodsList,
		setAddGoodsList,
		editGoodsList,
		setEditGoodsList,
		add,
		remove,
		verifyGoods,
		editDesc,
		addDesc,
		print,
		setPrint,
		historyVisible,
		setHistoryVisible,
		historyRow,
		setHistoryRow,
		buttonControl,
		setButtonControl,
		saveBtn,
		setSaveBtn,
		saveBtnLoading,
		setSaveBtnLoading,
		transformsToSDcGoods,
		applyList, 
		setApplyList,
		departmentOutValue, setDepartmentOutValue
	};
});
